import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Map a Firebase user to the shape the rest of the app expects.
const shapeUser = (fbUser) => ({
  uid: fbUser.uid,
  name: fbUser.displayName || fbUser.phoneNumber || (fbUser.email ? fbUser.email.split('@')[0] : 'Guest'),
  email: fbUser.email || null,
  phone: fbUser.phoneNumber || null,
  photoURL: fbUser.photoURL || null,
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Bridge to the global Firebase-backed AteliaAuth (loaded in index.html).
  // One shared session across the main app, the Stylist and the Studio.
  useEffect(() => {
    const AteliaAuth = window.AteliaAuth;
    if (!AteliaAuth) {
      // Auth module unavailable, fail open as an anonymous shopper.
      setLoading(false);
      return;
    }
    AteliaAuth.init({
      optional: true, // shoppers browse without a wall; sign-in is opt-in
      onSignedIn: async (fbUser) => {
        let base = shapeUser(fbUser);
        setUser(base);
        let idToken = null;
        try {
          idToken = await fbUser.getIdToken();
          setToken(idToken);
        } catch (_) {
          setToken(null);
        }
        // Upsert the uid-keyed profile server-side and enrich with role.
        if (idToken) {
          try {
            const apiBase = process.env.REACT_APP_BACKEND_URL || '';
            const res = await fetch(`${apiBase}/api/auth/me`, {
              headers: { Authorization: `Bearer ${idToken}` },
            });
            if (res.ok) {
              const me = await res.json();
              setUser({ ...base, ...me, name: me.name || base.name });
            }
          } catch (_) { /* non-fatal, keep the client-side identity */ }
        }
        setError(null);
        setLoading(false);
      },
      onSignedOut: () => {
        setUser(null);
        setToken(null);
        setLoading(false);
      },
    });
  }, []);

  // Open the shared on-brand Firebase login overlay (Phone OTP / Google).
  const openLogin = () => {
    if (window.AteliaAuth) window.AteliaAuth.openLogin();
  };

  // Kept for backwards-compatibility with old callers, both now route to the
  // unified overlay instead of the removed email/password flow.
  const login = () => { openLogin(); return { success: false, error: 'Use the Atelia login.' }; };
  const register = login;

  const logout = () => {
    if (window.AteliaAuth) {
      window.AteliaAuth.signOut();
    } else {
      setUser(null);
      setToken(null);
    }
  };

  const getAuthHeaders = () => {
    if (token) {
      return { 'Authorization': `Bearer ${token}` };
    }
    return {};
  };

  // Update name / phone: writes Firebase displayName (name) + the backend profile,
  // then reflects it locally. Used by the profile edit + "complete your profile".
  const updateProfile = async ({ name, phone }) => {
    const fb = window.AteliaAuth && window.AteliaAuth.user;
    if (!fb) return { success: false, error: 'Not signed in.' };
    try {
      if (name != null && name !== fb.displayName) {
        await fb.updateProfile({ displayName: name });
      }
      const freshToken = await fb.getIdToken(true);
      setToken(freshToken);
      const body = {};
      if (name != null) body.name = name;
      if (phone != null) body.phone = phone;
      const apiBase = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${apiBase}/api/auth/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${freshToken}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Could not save your profile.');
      }
      const me = await res.json();
      setUser((prev) => ({ ...(prev || {}), ...me, name: me.name || name || (prev && prev.name) }));
      return { success: true };
    } catch (e) {
      return { success: false, error: e.message };
    }
  };

  const value = {
    user,
    token,
    loading,
    error,
    isAuthenticated: !!user,
    openLogin,
    register,
    login,
    logout,
    getAuthHeaders,
    updateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
